import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { List, Loader2, AlertCircle, Pencil, Trash2 } from 'lucide-react';
import { laborScheduleService } from '../services/laborScheduleService';
import { formatCalendarOption, toDateInputValue } from '../utils/laborCalendarUtils';
import { LaborCalendarModalShell } from './LaborCalendarModalShell';
import { LaborConfirmDialog } from './LaborConfirmDialog';
import { LaborCreatePatternModal } from './LaborCreatePatternModal';

function formatPatternSummary(pattern) {
  const type = pattern.is_rotating ? `Rotativo · ${pattern.cycle_days} días` : 'Fijo';
  const status = pattern.active === false ? ' · inactivo' : '';
  return `${pattern.code} · ${type}${status}`;
}

export function LaborManagePatternsModal({
  isOpen,
  onClose,
  factoryId,
  factoryCode,
  defaultYear,
  defaultCalendarId = null,
  onAfterChange,
}) {
  const queryClient = useQueryClient();
  const [calendarId, setCalendarId] = useState('');
  const [error, setError] = useState(null);
  const [editingPatternId, setEditingPatternId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [patternToDelete, setPatternToDelete] = useState(null);

  const { data: calendars = [], isLoading: loadingCalendars } = useQuery({
    queryKey: ['labor-calendars', factoryId],
    queryFn: () => laborScheduleService.listCalendarsByFactory(factoryId),
    enabled: isOpen && Boolean(factoryId),
  });

  const {
    data: patternsRaw = [],
    isLoading: loadingPatterns,
    isFetching: fetchingPatterns,
    refetch,
  } = useQuery({
    queryKey: ['labor-patterns-by-calendar', calendarId],
    queryFn: () => laborScheduleService.listPatternsByCalendar(calendarId),
    enabled: isOpen && Boolean(calendarId),
  });

  const patterns = useMemo(
    () =>
      [...patternsRaw].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), 'es')
      ),
    [patternsRaw]
  );

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setEditingPatternId(null);
    setEditOpen(false);
    setPatternToDelete(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (defaultCalendarId) {
      setCalendarId(String(defaultCalendarId));
      return;
    }
    if (calendars.length === 0) return;
    const match = calendars.find((c) => Number(c.year) === Number(defaultYear));
    setCalendarId(String((match || calendars[0]).id));
  }, [isOpen, defaultCalendarId, calendars, defaultYear]);

  const deleteMutation = useMutation({
    mutationFn: (patternId) => laborScheduleService.deletePattern(patternId),
    onSuccess: async (_data, deletedPatternId) => {
      setPatternToDelete(null);
      await queryClient.invalidateQueries({ queryKey: ['labor-patterns-by-calendar'] });
      await queryClient.invalidateQueries({ queryKey: ['labor-calendar-pattern-list'] });
      await queryClient.invalidateQueries({ queryKey: ['labor-calendar-schedule'] });
      onAfterChange?.({ deletedPatternId });
      refetch();
    },
    onError: (err) => {
      setError(err?.message || 'No se pudo eliminar el patrón');
    },
  });

  const handleDeleteClick = (pattern) => {
    setError(null);
    setPatternToDelete(pattern);
  };

  const handleDeleteConfirm = () => {
    if (!patternToDelete) return;
    setError(null);
    deleteMutation.mutate(patternToDelete.id);
  };

  const handleEditSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ['labor-patterns-by-calendar'] });
    await queryClient.invalidateQueries({ queryKey: ['labor-calendar-pattern-list'] });
    await queryClient.invalidateQueries({ queryKey: ['labor-calendar-schedule'] });
    onAfterChange?.({});
    setEditOpen(false);
    setEditingPatternId(null);
    refetch();
  };

  const footer = (
    <button
      type="button"
      onClick={onClose}
      className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 text-sm"
    >
      Cerrar
    </button>
  );

  return (
    <>
      <LaborCalendarModalShell
        isOpen={isOpen}
        onClose={onClose}
        title="Gestionar patrones"
        icon={List}
        footer={footer}
        size="lg"
      >
        {error && (
          <div className="mb-4 p-3 rounded-lg border border-red-800 bg-red-900/20 flex gap-2 text-red-300 text-sm">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <p className="text-sm text-gray-400 mb-4">
          Fábrica <span className="text-white font-medium">{factoryCode || '—'}</span>. Año{' '}
          {defaultYear}.
        </p>

        <div className="mb-4">
          <label htmlFor="labor-pat-list-cal" className="block text-sm text-gray-300 mb-2">
            Calendario
          </label>
          {loadingCalendars ? (
            <p className="text-gray-500 text-sm">Cargando…</p>
          ) : (
            <select
              id="labor-pat-list-cal"
              value={calendarId}
              onChange={(e) => setCalendarId(e.target.value)}
              className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            >
              {calendars.map((cal) => (
                <option key={cal.id} value={cal.id}>
                  {formatCalendarOption(cal)}
                </option>
              ))}
            </select>
          )}
        </div>

        {loadingPatterns || fetchingPatterns ? (
          <p className="text-gray-500 text-sm flex items-center gap-2 py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" />
            Cargando patrones…
          </p>
        ) : patterns.length === 0 ? (
          <p className="text-amber-400 text-sm bg-amber-900/20 border border-amber-700/50 rounded-lg p-3">
            No hay patrones en este calendario. Créalos con el botón «Crear patrón».
          </p>
        ) : (
          <ul className="divide-y divide-gray-700 border border-gray-700 rounded-lg overflow-hidden">
            {patterns.map((pattern) => (
              <li
                key={pattern.id}
                className="flex flex-wrap items-center gap-3 px-3 py-3 bg-gray-900/40 hover:bg-gray-900/60"
              >
                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm font-medium text-white">{pattern.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{formatPatternSummary(pattern)}</p>
                  {pattern.cycle_start_date && (
                    <p className="text-xs text-gray-600 mt-0.5">
                      Inicio ciclo: {toDateInputValue(pattern.cycle_start_date)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingPatternId(String(pattern.id));
                      setEditOpen(true);
                    }}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-600 text-gray-200 hover:bg-gray-700 text-xs"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteClick(pattern)}
                    disabled={deleteMutation.isPending}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-red-800/60 text-red-300 hover:bg-red-900/30 text-xs disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Eliminar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </LaborCalendarModalShell>

      <LaborConfirmDialog
        isOpen={Boolean(patternToDelete)}
        onClose={() => !deleteMutation.isPending && setPatternToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Eliminar patrón"
        message={
          patternToDelete
            ? `¿Eliminar «${patternToDelete.name}»?\n${formatPatternSummary(patternToDelete)}\n\nSolo es posible si no tiene empleados asignados ni fichajes.`
            : ''
        }
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        isPending={deleteMutation.isPending}
      />

      <LaborCreatePatternModal
        isOpen={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditingPatternId(null);
        }}
        factoryId={factoryId}
        factoryCode={factoryCode}
        defaultYear={defaultYear}
        patternId={editingPatternId}
        onSuccess={handleEditSuccess}
      />
    </>
  );
}
